import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin } from "lucide-react";

interface MunicipalitySelectorProps {
  value: string | null;
  onChange: (municipalityId: string) => void;
}

export const MunicipalitySelector = ({ value, onChange }: MunicipalitySelectorProps) => {
  const { data: municipalities, isLoading } = useQuery({
    queryKey: ['municipalities-public'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('municipalities')
        .select('id, name, logo_url')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="w-full max-w-md">
        <p className="text-white text-center text-sm">Laddar kommuner...</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md space-y-2">
      <label className="text-white text-sm font-medium flex items-center gap-2">
        <MapPin className="h-4 w-4" />
        Välj kommun
      </label>
      <Select value={value || undefined} onValueChange={onChange}>
        <SelectTrigger className="w-full bg-white/10 border-white/20 text-white backdrop-blur-sm hover:bg-white/15 transition-colors">
          <SelectValue placeholder="Välj din kommun" />
        </SelectTrigger>
        <SelectContent className="bg-card border-border">
          {municipalities?.map((municipality) => (
            <SelectItem 
              key={municipality.id} 
              value={municipality.id}
              className="cursor-pointer hover:bg-accent"
            >
              <div className="flex items-center gap-2">
                {municipality.logo_url && (
                  <img 
                    src={municipality.logo_url} 
                    alt={municipality.name}
                    className="h-5 w-5 object-contain"
                  />
                )}
                <span>{municipality.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-white/70 text-xs">
        Välj vilken kommun du vill logga in på. Olika kommuner kan ha olika registreringsinställningar.
      </p>
    </div>
  );
};
